import { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Custom hook to fetch CMS page content and components
 * @param {string} pagePath - The path of the page (e.g., '/our-mission')
 * @returns {Object} - { pageData, pageComponents, loading, error }
 */
export const useCMSPage = (pagePath) => {
  const [pageData, setPageData] = useState(null);
  const [pageComponents, setPageComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageContent = async () => {
      if (!pagePath) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Ensure path starts with /
        const normalizedPath = pagePath.startsWith('/') ? pagePath : `/${pagePath}`;
        
        const response = await axios.get(
          `${API_BASE_URL}/api/public/cms/pages${normalizedPath}`
        );
        
        if (response.data.success && response.data.page) {
          const page = response.data.page;
          setPageData(page);
          
          // Fetch component details if page has components
          if (page.components && page.components.length > 0) {
            try {
              const componentsResponse = await axios.get(`${API_BASE_URL}/api/public/cms/components`);
              const allComponents = componentsResponse.data.components || [];
              
              // Map page component references to full component data
              const resolvedComponents = page.components.map(ref => {
                const fullComponent = allComponents.find(c => c.id === ref.component_id);
                return {
                  ...ref,
                  component: fullComponent
                };
              }).filter(c => c.component); // Filter out any not found
              
              setPageComponents(resolvedComponents);
            } catch (compErr) {
              console.error('Error fetching components:', compErr);
              setPageComponents([]);
            }
          } else {
            setPageComponents([]);
          }
        } else {
          // No CMS content found - page will use default/hardcoded content
          setPageData(null);
          setPageComponents([]);
        }
      } catch (err) {
        console.error('Error fetching CMS page content:', err);
        setError(err);
        setPageData(null);
        setPageComponents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPageContent();
  }, [pagePath]);

  return { pageData, pageComponents, loading, error };
};

export default useCMSPage;
